/**
 * FeedbackPage — отзывы покупателей с экрана «Hotovo» на киосках.
 *
 * Итог за период, разбивка по машинам, причины плохих отзывов и сама лента.
 * Песочница (384 и служебные номера) не считается — там кнопки жмём мы сами.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getFeedback, getFeedbackSummary,
  type FeedbackRating, type FeedbackReview, type FeedbackSummary,
} from '../api/client';
import { FeedbackBar, RatingBadge, reasonLabel, RATING_META } from '../components/FeedbackBar';

const PERIODS: Array<{ days: number; label: string }> = [
  { days: 1, label: 'Today' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 0, label: 'All time' },
];

const LABEL_STYLE = { fontSize: '0.78rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em', marginBottom: 8 };

export function FeedbackPage() {
  const [params, setParams] = useSearchParams();
  const [days, setDays] = useState(30);
  const machineParam = Number(params.get('machine'));
  const machineId = Number.isInteger(machineParam) && machineParam > 0 ? machineParam : undefined;
  const [rating, setRating] = useState<FeedbackRating | ''>('');
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [reviews, setReviews] = useState<FeedbackReview[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    Promise.all([
      getFeedbackSummary(days),
      getFeedback({ days, machineId, rating: rating || undefined, limit: 300 }),
    ])
      .then(([s, r]) => { if (alive) { setSummary(s); setReviews(r); } })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'Failed to load reviews'); });
    return () => { alive = false; };
  }, [days, machineId, rating]);

  function setMachine(id: number | undefined) {
    const next = new URLSearchParams(params);
    if (id) next.set('machine', String(id)); else next.delete('machine');
    setParams(next, { replace: true });
  }

  const period = PERIODS.find((p) => p.days === days)?.label ?? `${days} days`;

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Customer Reviews</h2>
        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
          {PERIODS.map((p) => (
            <button
              key={p.days}
              className={`btn btn-sm ${p.days === days ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDays(p.days)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="card mb-6"><div className="card-body">
          <div style={{ color: 'var(--color-offline)', fontWeight: 500 }}>Error: {error}</div>
        </div></div>
      )}

      {summary && (
        <div className="mb-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          <div className="card">
            <div className="card-body" style={{ padding: '16px 20px' }}>
              <div className="text-muted" style={LABEL_STYLE}>All machines · {period} · {summary.totals.total} reviews</div>
              <FeedbackBar totals={summary.totals} />
            </div>
          </div>
          <div className="card">
            <div className="card-body" style={{ padding: '16px 20px' }}>
              <div className="text-muted" style={LABEL_STYLE}>Why bad</div>
              {summary.badReasons.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>No bad reviews 🎉</p>
              ) : summary.badReasons.map((r) => (
                <div key={String(r.reason)} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', padding: '2px 0' }}>
                  <span>{reasonLabel(r.reason)}</span>
                  <span style={{ fontWeight: 600 }}>{r.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {summary && summary.byMachine.length > 0 && (
        <div className="card mb-6">
          <div className="card-header">By machine · {period}</div>
          <div className="card-body">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Machine</th>
                    <th>{RATING_META.good.icon} Good</th>
                    <th>{RATING_META.ok.icon} Neutral</th>
                    <th>{RATING_META.bad.icon} Bad</th>
                    <th>Total</th>
                    <th>Last bad</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byMachine.map((m) => (
                    <tr
                      key={m.machineId}
                      style={{ cursor: 'pointer', background: machineId === m.machineId ? 'rgba(59,130,246,0.08)' : undefined }}
                      onClick={() => setMachine(machineId === m.machineId ? undefined : m.machineId)}
                      title="Show only this machine's reviews"
                    >
                      <td>
                        <Link to={`/machines/${m.machineId}`} onClick={(e) => e.stopPropagation()}>
                          {m.machineName ? `${m.machineName} (#${m.machineId})` : `#${m.machineId}`}
                        </Link>
                      </td>
                      <td>{m.good}</td>
                      <td>{m.ok}</td>
                      <td style={{ color: m.bad > 0 ? RATING_META.bad.color : undefined, fontWeight: m.bad > 0 ? 600 : undefined }}>{m.bad}</td>
                      <td>{m.total}</td>
                      <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {m.lastBadAt ? new Date(m.lastBadAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <span>
            Reviews{machineId ? ` · machine #${machineId}` : ''}
            {machineId && (
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={() => setMachine(undefined)}>
                All machines
              </button>
            )}
          </span>
          <select value={rating} onChange={(e) => setRating(e.target.value as FeedbackRating | '')}>
            <option value="">All ratings</option>
            <option value="bad">👎 Bad only</option>
            <option value="ok">😐 Neutral only</option>
            <option value="good">👍 Good only</option>
          </select>
        </div>
        <div className="card-body">
          {reviews === null ? (
            <div className="empty-state"><p className="text-muted">Loading…</p></div>
          ) : reviews.length === 0 ? (
            <div className="empty-state"><p className="text-muted">No reviews for this filter.</p></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Rating</th>
                    <th>Reason</th>
                    <th>Machine</th>
                    <th>Order</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((r) => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.at).toLocaleString()}</td>
                      <td><RatingBadge rating={r.rating} /></td>
                      <td>{r.rating === 'bad' ? reasonLabel(r.reason) : <span className="text-muted">—</span>}</td>
                      <td>
                        <Link to={`/machines/${r.machineId}`}>
                          {r.machineName ? `${r.machineName} (#${r.machineId})` : `#${r.machineId}`}
                        </Link>
                      </td>
                      <td>{r.code ? <Link to={`/sessions?code=${r.code}`}>{r.code}</Link> : <span className="text-muted">—</span>}</td>
                      <td className="text-muted">{r.source ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
